import * as Dialog from '@radix-ui/react-dialog';
import type { HTMLAttributes } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { ModalDialogBody } from '~/chords/ModalDialog/components/ModalDialogBody';
import { ModalDialogClose } from '~/chords/ModalDialog/components/ModalDialogClose';
import { ModalDialogContent } from '~/chords/ModalDialog/components/ModalDialogContent';
import { ModalDialogFooter } from '~/chords/ModalDialog/components/ModalDialogFooter';
import { ModalDialogHeader } from '~/chords/ModalDialog/components/ModalDialogHeader';
import { ModalDialogTrigger } from '~/chords/ModalDialog/components/ModalDialogTrigger';
import {
  CONTENT_CLASS,
  OVERLAY_CLASS,
} from '~/chords/ModalDialog/components/styles';
import { renderSlot } from '~/chords/ModalDialog/utils/renderSlot';

export interface ModalDialogProps
  extends Dialog.DialogProps,
    ComponentProps<HTMLAttributes<HTMLDivElement>> {}

export function ModalDialog({ children, ...props }: ModalDialogProps) {
  const className = getClassNameOverride(props, 'ModalDialog');
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(
    props?.open || props?.defaultOpen || false,
  );

  useEffect(() => {
    if (props.open !== undefined) {
      setIsOpen(props.open);
    }
  }, [props.open]);

  const toggleIsOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      props.onOpenChange?.(open);
    },
    [props],
  );

  const closeModalDialog = useCallback(() => {
    toggleIsOpen(false);
  }, [toggleIsOpen]);

  const handleClose = useCallback(() => {
    overlayRef.current?.classList.remove(OVERLAY_CLASS.show);
    contentRef.current?.classList.remove(CONTENT_CLASS.show);

    contentRef.current?.addEventListener('animationend', closeModalDialog, {
      once: true,
    });

    requestAnimationFrame(() => {
      // Animate the overlay and dialog out of view as they close.
      overlayRef.current?.classList.add(OVERLAY_CLASS.hide);
      contentRef.current?.classList.add(
        CONTENT_CLASS.disable,
        CONTENT_CLASS.hide,
      );
    });
  }, [overlayRef, contentRef, closeModalDialog]);

  useEffect(() => {
    const dialogContent = contentRef.current;

    return () => {
      if (!isOpen) {
        dialogContent?.removeEventListener('animationend', closeModalDialog);
      }
    };
  }, [contentRef, isOpen, closeModalDialog]);

  const handleOpenChange = useCallback(
    (open: boolean) => (open ? toggleIsOpen(true) : handleClose()),
    [handleClose, toggleIsOpen],
  );

  return (
    <Dialog.Root {...props} open={isOpen} onOpenChange={handleOpenChange}>
      {renderSlot(ModalDialog.Trigger, children)}
      <ModalDialogContent
        overlayRef={overlayRef}
        contentRef={contentRef}
        className={className}
      >
        {children}
      </ModalDialogContent>
    </Dialog.Root>
  );
}

ModalDialog.displayName = 'ModalDialog';

ModalDialog.Trigger = ModalDialogTrigger;
ModalDialog.Header = ModalDialogHeader;
ModalDialog.Body = ModalDialogBody;
ModalDialog.Footer = ModalDialogFooter;
ModalDialog.Close = ModalDialogClose;
